// What the Page Access screen and the User Manager offer: every role-gated
// route (named and grouped like the nav menu where it appears there) and
// every role anyone can hold.
import { routes } from "../../../components/Routes/RouteHandler";
import {
  NAV_SECTIONS,
  sectionGroups,
} from "../../../components/Navigation/SideNav";
import { roles as BUILT_IN_ROLES } from "../../../utils/NodeRoles";
import { grantKey } from "../../../utils/routeAccess";

// Pages that still decide part of what they show by role alone, so a grant
// opens the page but not everything on it. Shown on the page's access panel.
export const PAGE_NOTES = {
  "/signage":
    "The signage API still decides what each person sees by role: Admin and Executive Team get every branch, General Managers their own. Someone granted this page without one of those roles can open it but won't see any displays.",
  "/sbcfadmin":
    "Its tabs still follow SBCF roles: the spec sheet queue needs Admin, SBCF Assigner, or SBCF Team, and form admin needs Admin or SBCF Admin. A grant opens the page; give one of those roles for the tabs.",
};

const OTHER_SECTION = "Other pages";

const cleanRouteName = (name) =>
  String(name || "")
    .replace(/^Big C Node\s*-\s*/i, "")
    .trim();

const withSlash = (path) => (path.startsWith("/") ? path : `/${path}`);

// Built on demand, not at import: RouteHandler imports these screens, so the
// route table isn't initialized yet when this module first loads.
export const buildPageCatalog = () => {
  const nav = new Map();
  NAV_SECTIONS.forEach((section) =>
    sectionGroups(section).forEach((group) =>
      group.items.forEach((item) => {
        const key = grantKey(item.path);
        if (!nav.has(key)) {
          nav.set(key, { name: item.name, section: section.title });
        }
      })
    )
  );

  const pages = new Map();
  routes.forEach((route) => {
    if (!route.roles?.length || route.roles.includes("*")) return;
    const key = grantKey(route.path);
    if (key === "/") return;
    const isDetail = route.path.includes(":");
    const navEntry = nav.get(key);
    const existing = pages.get(key);

    if (existing) {
      existing.paths.push(withSlash(route.path));
      existing.roles = [...new Set([...existing.roles, ...route.roles])];
      // The param-free route names the page and is the link to it.
      if (!isDetail && !existing.redirect) {
        existing.redirect = withSlash(route.path);
        if (!navEntry) existing.name = cleanRouteName(route.name) || key;
      }
      existing.hasDetail = existing.hasDetail || isDetail;
      return;
    }

    pages.set(key, {
      key,
      name: navEntry?.name || cleanRouteName(route.name) || key,
      section: navEntry?.section || OTHER_SECTION,
      paths: [withSlash(route.path)],
      roles: [...route.roles],
      redirect: isDetail ? null : withSlash(route.path),
      hasDetail: isDetail,
    });
  });

  const sectionOrder = [...NAV_SECTIONS.map((s) => s.title), OTHER_SECTION];
  return [...pages.values()].sort(
    (a, b) =>
      sectionOrder.indexOf(a.section) - sectionOrder.indexOf(b.section) ||
      a.name.localeCompare(b.name)
  );
};

// Every role that exists anywhere: the built-in list, roles pages ask for
// (e.g. "Web Admin"), roles created on Page Access, and anything already on
// a user — so nothing a user holds is ever invisible here.
export const buildRoleCatalog = ({ customRoles = [], users = [], pages = [] }) => {
  const custom = new Map(customRoles.map((r) => [r.name, r]));
  const names = new Set([
    ...BUILT_IN_ROLES,
    ...pages.flatMap((p) => p.roles),
    ...custom.keys(),
    ...users.flatMap((u) => u.roles || []),
  ]);
  names.delete("*");
  return [...names]
    .map((name) => ({
      name,
      custom: custom.has(name),
      description: custom.get(name)?.description || "",
      createdBy: custom.get(name)?.createdBy || null,
      createdAt: custom.get(name)?.createdAt || null,
      builtIn: BUILT_IN_ROLES.includes(name),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export { BUILT_IN_ROLES };
